import { create, toBinary } from '@bufbuild/protobuf'
import type { GenerationSite } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { UpdateGenerationSiteRequestSchema } from '@ebalance/proto/bffuser/v1/occto_master_service_pb'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { AreaChangeConfirmDialog } from '~/components/common/AreaChangeConfirmDialog'
import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { entitySourceOf, useEditModalForm } from '~/hooks/useEditModalForm'
import {
  type AreaChangeConfirmation,
  siteAreaChangeConfirmationOf,
} from '~/models/areaChange'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'
import { GenerationSiteFormFields } from '~/pages/AdminPlantUnitMaster/components/GenerationSiteFormFields'
import { useGetGenerationSite } from '~/pages/AdminPlantUnitMaster/hooks/useGetGenerationSite'
import { useUpdateGenerationSite } from '~/pages/AdminPlantUnitMaster/hooks/useUpdateGenerationSite'
import {
  GENERATION_SITE_ERROR_KEY_PREFIX,
  GENERATION_SITE_SAVE_ERROR_KEYS,
  GENERATION_SITE_UNKNOWN_ERROR_KEY,
  resolveGenerationSiteSaveError,
} from '~/pages/AdminPlantUnitMaster/models/serverError'
import {
  type GenerationSiteFormInput,
  type GenerationSiteFormValues,
  buildGenerationSiteFormSchema,
  toOptionalText,
} from '~/pages/AdminPlantUnitMaster/models/siteForm'
import type { GenerationSiteRow } from '~/pages/AdminPlantUnitMaster/models/siteRows'

type Props = {
  isOpen: boolean
  row: GenerationSiteRow
  onClose: () => void
}

const toFormValues = (site: GenerationSite): GenerationSiteFormInput => ({
  siteName: site.siteName,
  siteNameAbbr: site.siteNameAbbr ?? '',
  areaCode: site.areaCode,
  note: site.note ?? '',
})

/**
 * 発電所 (サイト) の編集ダイアログ。開いた時に最新を取り直し (`useGetGenerationSite`)、
 * その内容で form を埋める (一覧の行より新しいことがある)。
 */
export const EditGenerationSiteModal = ({
  isOpen,
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateGenerationSite()
  const getQuery = useGetGenerationSite(row.id)
  const { isError, error: fetchError } = getQuery
  const {
    confirmation,
    confirm,
    cancelConfirmation,
    isSubmitting,
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    isDirty,
    loadStatus,
  } = useEditModalForm<
    GenerationSite,
    GenerationSiteFormInput,
    GenerationSiteFormValues,
    AreaChangeConfirmation
  >({
    source: entitySourceOf(getQuery, (d) => d.generationSite),
    toFormValues,
    schema: buildGenerationSiteFormSchema(t),
    defaultValues: {
      siteName: row.siteName,
      siteNameAbbr: row.siteNameAbbr,
      areaCode: row.areaCode,
      note: row.note,
    },
    confirmationOf: (err) => siteAreaChangeConfirmationOf(t, err),
    prepareSubmit: (values, { expectedUpdatedAt }) => {
      const request = create(UpdateGenerationSiteRequestSchema, {
        id: row.id,
        siteName: values.siteName,
        siteNameAbbr: toOptionalText(values.siteNameAbbr),
        areaCode: values.areaCode,
        areaChangeConfirmed: false,
        note: toOptionalText(values.note),
        expectedUpdatedAt,
      })
      return {
        identity: toBinary(UpdateGenerationSiteRequestSchema, request),
        send: (areaChangeConfirmed) =>
          mutateAsync({ ...request, areaChangeConfirmed }),
      }
    },
    onClose,
    customErrorKeys: GENERATION_SITE_SAVE_ERROR_KEYS,
    unknownErrorKey: GENERATION_SITE_UNKNOWN_ERROR_KEY,
    resolveDetailedError: resolveGenerationSiteSaveError,
  })

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending || isSubmitting}
      loadError={
        isError ? (
          <Alert
            description={mapMasterGetError(
              t,
              fetchError,
              GENERATION_SITE_ERROR_KEY_PREFIX,
            )}
            status="error"
          />
        ) : undefined
      }
      loadStatus={loadStatus}
      onClose={() => {
        if (!isSubmitting) handleClose()
      }}
      serverError={serverError}
      size="md"
      title={t('page:adminPlantUnitMaster.site.editTitle')}
    >
      <GenerationSiteFormFields
        control={control}
        disabled={isPending || isSubmitting}
        errors={errors}
      />
      {confirmation !== undefined && (
        <AreaChangeConfirmDialog
          confirmation={confirmation}
          isLoading={isPending}
          onCancel={cancelConfirmation}
          onConfirm={confirm}
        />
      )}
    </CrudModalLayout>
  )
}
